import { useEffect, useState } from "react";
import { useStation } from "@/context/StationContext";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { fmtMYR, fmtL } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Fuel, Trash2, MapPin, RefreshCw, Loader2, DollarSign, Save, Pencil, X, TrendingUp, Wrench, ArrowRight } from "lucide-react";

const PRODUCTS = [{ value: "RON95", label: "RON95" }, { value: "DIESEL", label: "DIESEL" }, { value: "EURO5", label: "Euro 5 (B7)" }];
const card = "rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl";
const PUMP_COLORS = { RON95: "#059669", DIESEL: "#D97706", EURO5: "#2563EB" };

const PumpCard = ({ pump, onSaved, onDelete, onEdit, totals, month }) => {
  const [retail, setRetail] = useState(pump.retail_price ?? "");
  const [cost, setCost] = useState(pump.purchase_price ?? "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setRetail(pump.retail_price ?? ""); setCost(pump.purchase_price ?? ""); }, [pump.retail_price, pump.purchase_price]);
  const dirty = Number(retail) !== Number(pump.retail_price) || Number(cost) !== Number(pump.purchase_price);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/pumps/${pump.id}/price`, { retail_price: Number(retail || 0), purchase_price: Number(cost || 0) });
      toast.success(`${pump.label} saved to database`);
      onSaved();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    setSaving(false);
  };
  return (
    <div className="rounded-xl border border-[#1F2937] bg-[#0B0F17] p-4" data-testid={`pump-${pump.id}`}>
      <div className="flex items-center justify-between">
        <span className="font-medium">{pump.label}</span>
        <div className="flex items-center gap-2">
          <button onClick={() => onEdit(pump)} data-testid={`edit-pump-${pump.id}`} className="text-[#9CA3AF] hover:text-emerald-400"><Pencil className="w-3.5 h-3.5" /></button>
          <button onClick={() => onDelete(pump.id)} data-testid={`delete-pump-${pump.id}`} className="text-[#9CA3AF] hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      </div>
      <div className="text-xs font-mono uppercase tracking-wider mt-1" style={{ color: PUMP_COLORS[pump.product] }}>{pump.product}</div>
      <div className="mt-3 space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#9CA3AF] w-20">Retail /L</span>
          <Input value={retail} type="number" step="0.001" data-testid={`retail-price-${pump.id}`}
            onChange={(e) => setRetail(e.target.value)} className="h-8 bg-[#111827] border-[#374151] text-white text-sm" />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#9CA3AF] w-20">Cost /L</span>
          <Input value={cost} type="number" step="0.001" data-testid={`cost-price-${pump.id}`}
            onChange={(e) => setCost(e.target.value)} className="h-8 bg-[#111827] border-[#374151] text-white text-sm" />
        </div>
        <div className="text-xs text-[#9CA3AF] flex items-center gap-1 pt-1">
          <DollarSign className="w-3 h-3 text-emerald-400" /> Margin {retail !== "" && cost !== "" ? fmtMYR(Number(retail) - Number(cost)) : "—"}/L · Tank {pump.tank_number ? `#${pump.tank_number} · ` : ""}{pump.tank_capacity}L
        </div>
        <div className="text-xs flex items-center gap-1 text-[#9CA3AF]" data-testid={`hose-totals-${pump.id}`}>
          <TrendingUp className="w-3 h-3 text-blue-400" /> {month || "This month"}: <b className="text-white metric-value">{fmtL(totals?.liters || 0)}</b> · <b className="text-white metric-value">{fmtMYR(totals?.sales || 0)}</b>
        </div>
        <Button size="sm" onClick={save} disabled={!dirty || saving} data-testid={`save-pump-${pump.id}`}
          className="w-full mt-1 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold disabled:opacity-40">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} {dirty ? "Save changes" : "Saved"}
        </Button>
      </div>
    </div>
  );
};

export default function Stations() {
  const { stationId } = useStation();
  const [stations, setStations] = useState([]);
  const [pumps, setPumps] = useState({});
  const [hoseTotals, setHoseTotals] = useState({});
  const [totalsMonth, setTotalsMonth] = useState("");
  const [loading, setLoading] = useState(true);

  const [stForm, setStForm] = useState({ name: "", location: "", code: "" });
  const [stOpen, setStOpen] = useState(false);

  const [pumpOpen, setPumpOpen] = useState(false);
  const newHose = (i) => ({ label: `Hose ${i}`, product: "RON95", tank_number: "", tank_capacity: "", retail_price: "", purchase_price: "" });
  const [pumpForm, setPumpForm] = useState({ station_id: "", pump_no: "", hoses: [newHose(1)] });
  const addHose = () => setPumpForm((f) => (f.hoses.length >= 4 ? f : { ...f, hoses: [...f.hoses, newHose(f.hoses.length + 1)] }));
  const removeHose = (i) => setPumpForm((f) => ({ ...f, hoses: f.hoses.length > 1 ? f.hoses.filter((_, idx) => idx !== i) : f.hoses }));
  const setHose = (i, patch) => setPumpForm((f) => ({ ...f, hoses: f.hoses.map((h, idx) => (idx === i ? { ...h, ...patch } : h)) }));
  const groupHoses = (list) => {
    const groups = {};
    (list || []).forEach((p) => {
      const key = p.pump_no || p.label || p.id;
      if (!groups[key]) groups[key] = { key, pump_no: p.pump_no || "", display: p.pump_no || p.label || "—", grouped: !!p.pump_no, hoses: [] };
      groups[key].hoses.push(p);
    });
    return Object.values(groups);
  };
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ id: "", label: "", product: "RON95", pump_no: "", tank_number: "", tank_capacity: "" });

  const [prices, setPrices] = useState(null);
  const [fetching, setFetching] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/stations");
    setStations(data);
    const dm = await api.get("/data-months").then((r) => r.data || []).catch(() => []);
    const month = dm.length ? dm[dm.length - 1] : new Date().toISOString().slice(0, 7);
    setTotalsMonth(month);
    const map = {}, tmap = {};
    await Promise.all(data.map(async (s) => {
      const [r, t] = await Promise.all([
        api.get(`/stations/${s.id}/pumps`),
        api.get(`/pumps/hose-totals?station_id=${s.id}&month=${month}`).catch(() => ({ data: { totals: {} } })),
      ]);
      map[s.id] = r.data;
      tmap[s.id] = t.data.totals || {};
    }));
    setPumps(map);
    setHoseTotals(tmap);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const createStation = async (e) => {
    e.preventDefault();
    try {
      await api.post("/stations", stForm);
      toast.success("Station added");
      setStForm({ name: "", location: "", code: "" });
      setStOpen(false);
      load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const createPump = async (e) => {
    e.preventDefault();
    if (!pumpForm.pump_no) return toast.error("Enter a pump number");
    try {
      await api.post("/pumps/group", {
        station_id: pumpForm.station_id,
        pump_no: pumpForm.pump_no,
        hoses: pumpForm.hoses.map((h) => ({
          label: h.label, product: h.product, tank_number: h.tank_number,
          tank_capacity: Number(h.tank_capacity || 0), retail_price: Number(h.retail_price || 0),
          purchase_price: Number(h.purchase_price || 0),
        })),
      });
      toast.success(`Pump ${pumpForm.pump_no} added · ${pumpForm.hoses.length} hose${pumpForm.hoses.length === 1 ? "" : "s"}`);
      setPumpOpen(false);
      setPumpForm({ station_id: "", pump_no: "", hoses: [newHose(1)] });
      load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const delStation = async (id) => {
    if (!window.confirm("Delete this station and all its pumps?")) return;
    await api.delete(`/stations/${id}`);
    toast.success("Station deleted");
    load();
  };

  const delPump = async (id) => {
    await api.delete(`/pumps/${id}`);
    toast.success("Hose removed");
    load();
  };

  const delPumpGroup = async (stationId, pumpNo) => {
    if (!window.confirm(`Delete Pump ${pumpNo} and all its hoses?`)) return;
    await api.delete(`/pumps/group?station_id=${stationId}&pump_no=${encodeURIComponent(pumpNo)}`);
    toast.success("Pump removed");
    load();
  };

  const openEdit = (p) => {
    setEditForm({ id: p.id, label: p.label || "", product: p.product || "RON95", pump_no: p.pump_no || "", tank_number: p.tank_number || "", tank_capacity: p.tank_capacity ?? "" });
    setEditOpen(true);
  };
  const savePumpEdit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/pumps/${editForm.id}`, {
        label: editForm.label, product: editForm.product, pump_no: editForm.pump_no,
        tank_number: editForm.tank_number, tank_capacity: Number(editForm.tank_capacity || 0),
      });
      toast.success("Hose updated");
      setEditOpen(false);
      load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const fetchPrices = async () => {
    setFetching(true);
    try {
      const { data } = await api.get("/fuel-prices");
      setPrices(data);
      if (data.status === "ok") toast.success("Latest prices fetched from web");
      else toast.warning("Could not fetch live prices — enter manually");
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    setFetching(false);
  };

  const applyPrices = async () => {
    try {
      const { data } = await api.post("/pumps/apply-prices", { prices: prices.prices });
      toast.success(`Applied to ${data.updated} pump${data.updated === 1 ? "" : "s"}`);
      load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const [wiping, setWiping] = useState(false);
  const [wipePw, setWipePw] = useState("");
  const [seeding, setSeeding] = useState(false);
  const loadHistorical = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/seed-historical");
      toast.success(`Loaded ${data.readings} historical records (${data.products.join(", ")})`);
      load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    setSeeding(false);
  };
  const resetData = async () => {
    setWiping(true);
    try {
      await api.post("/admin/reset-data", { password: wipePw });
      toast.success("All station data cleared");
      setWipePw("");
      load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    setWiping(false);
  };

  const [lastSync, setLastSync] = useState(null);
  const [syncing, setSyncing] = useState(false);
  useEffect(() => { api.get("/price-sync/last").then((r) => setLastSync(r.data)).catch(() => {}); }, []);
  const runSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/price-sync/run");
      if (data.updated > 0) toast.success(`Weekly sync applied to ${data.updated} pump${data.updated === 1 ? "" : "s"}`);
      else toast.warning("No online prices found — pumps unchanged");
      const r = await api.get("/price-sync/last"); setLastSync(r.data);
      load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    setSyncing(false);
  };

  const [audit, setAudit] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [reFrom, setReFrom] = useState("");
  const [reTo, setReTo] = useState("");
  const [reassigning, setReassigning] = useState(false);
  const loadAudit = async () => {
    setAuditLoading(true);
    try { const { data } = await api.get("/stations/data-audit"); setAudit(data); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    setAuditLoading(false);
  };
  const runReassign = async () => {
    if (!reFrom || !reTo) return toast.error("Pick both the source and target station");
    if (reFrom === reTo) return toast.error("Source and target must differ");
    if (!window.confirm("Move all records from the selected source into the target station? This affects every user.")) return;
    setReassigning(true);
    try {
      const { data } = await api.post("/stations/reassign-data", { from_station_id: reFrom, to_station_id: reTo });
      toast.success(`Moved ${data.total} record${data.total === 1 ? "" : "s"} into ${data.target}${data.skipped_duplicates ? ` · ${data.skipped_duplicates} duplicate${data.skipped_duplicates === 1 ? "" : "s"} dropped` : ""}`);
      setReFrom(""); setReTo(""); await loadAudit(); load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    setReassigning(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-emerald-400 mb-1">Configuration</div>
          <h1 className="font-[Outfit] font-bold text-3xl sm:text-4xl tracking-tight">Stations &amp; Pumps</h1>
        </div>
        <Dialog open={stOpen} onOpenChange={setStOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-station-button" className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">
              <Plus className="w-4 h-4 mr-2" /> Add Station
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#111827] border-[#374151] text-white">
            <DialogHeader><DialogTitle>Add Station</DialogTitle></DialogHeader>
            <form onSubmit={createStation} className="space-y-4" data-testid="station-form">
              <div><Label className="text-[#9CA3AF]">Name</Label>
                <Input data-testid="station-name-input" required value={stForm.name}
                  onChange={(e) => setStForm({ ...stForm, name: e.target.value })}
                  className="mt-1.5 bg-[#0B0F17] border-[#374151]" /></div>
              <div><Label className="text-[#9CA3AF]">Location</Label>
                <Input data-testid="station-location-input" value={stForm.location}
                  onChange={(e) => setStForm({ ...stForm, location: e.target.value })}
                  className="mt-1.5 bg-[#0B0F17] border-[#374151]" /></div>
              <p className="text-xs text-[#6B7280]">A station code is generated automatically (00001, 00002, …).</p>
              <DialogFooter>
                <Button type="submit" data-testid="station-submit-button" className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">Create</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Wednesday price widget */}
      <div className={`${card} p-5 sm:p-6`} data-testid="fuel-price-widget">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-amber-400 text-sm font-medium">
              <RefreshCw className="w-4 h-4" /> Wednesday Fuel Price Sync
            </div>
            <p className="text-xs text-[#9CA3AF] mt-1">Retail &amp; cost change every Wednesday. Fetch the latest prices below — each pump keeps its <b className="text-white">fixed margin</b> (cost = retail − margin). Auto-syncs every Thursday 00:00 (MYT).</p>
            {lastSync?.finished_at && (
              <p className={`text-[11px] mt-1 ${lastSync.status === "no_prices" ? "text-amber-400" : "text-emerald-400"}`} data-testid="last-sync-info">
                {lastSync.status === "no_prices"
                  ? `Last sync attempt: ${new Date(lastSync.finished_at).toLocaleString()} — no online prices found`
                  : `Last auto-sync: ${new Date(lastSync.finished_at).toLocaleString()} · ${lastSync.updated} pump${lastSync.updated === 1 ? "" : "s"}`}
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={fetchPrices} disabled={fetching} data-testid="wednesday-price-fetch-button"
              variant="outline" className="border-amber-500/40 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20">
              {fetching ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-4 h-4 mr-2" /> Fetch latest prices</>}
            </Button>
            <Button onClick={runSync} disabled={syncing} data-testid="run-weekly-sync-button"
              className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">
              {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Run weekly sync now"}
            </Button>
          </div>
        </div>
        {prices && (
          <div className="mt-4 flex flex-wrap items-center gap-3 text-sm" data-testid="fetched-prices">
            {prices.status === "ok" ? (
              <>
                {Object.entries(prices.prices).map(([k, v]) => (
                  <span key={k} className="font-mono px-3 py-1.5 rounded-lg bg-[#0B0F17] border border-[#374151]">
                    {k}: <span className="text-emerald-400 font-semibold">{fmtMYR(v)}</span>/L
                  </span>
                ))}
                <Button size="sm" onClick={applyPrices} data-testid="apply-prices-button"
                  className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">
                  Apply to all pumps
                </Button>
              </>
            ) : (
              <span className="text-amber-400 text-xs">{prices.note}</span>
            )}
            {prices.source && <span className="text-xs text-[#9CA3AF] self-center">source: {prices.source}</span>}
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-emerald-400 animate-spin" /></div>
      ) : stations.filter((s) => stationId === "all" || s.id === stationId).length === 0 ? (
        <div className={`${card} p-12 text-center`}>
          <Fuel className="w-10 h-10 text-[#374151] mx-auto mb-3" />
          <p className="text-[#9CA3AF]">No stations yet. Add your first station to get started.</p>
        </div>
      ) : (
        <div className="space-y-5">
          {stations.filter((s) => stationId === "all" || s.id === stationId).map((s) => (
            <div key={s.id} className={`${card} p-5 sm:p-6`} data-testid={`station-card-${s.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-[Outfit] font-semibold text-xl">{s.name} {s.code && <span className="text-xs font-mono text-emerald-400 ml-1">{s.code}</span>}</h3>
                  {s.location && <div className="flex items-center gap-1 text-sm text-[#9CA3AF] mt-1"><MapPin className="w-3.5 h-3.5" /> {s.location}</div>}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" data-testid={`add-pump-button-${s.id}`}
                    onClick={() => { setPumpForm({ station_id: s.id, pump_no: "", hoses: [newHose(1)] }); setPumpOpen(true); }}
                    className="border-[#374151] bg-transparent hover:bg-[#1F2937] text-white">
                    <Plus className="w-4 h-4 mr-1" /> Pump
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => delStation(s.id)} data-testid={`delete-station-${s.id}`}
                    className="border-red-500/30 bg-transparent hover:bg-red-500/10 text-red-400">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="mt-4 space-y-4">
                {groupHoses(pumps[s.id]).map((g) => (
                  <div key={g.key} className="rounded-xl border border-[#1F2937] bg-[#0B0F17]/40 p-3" data-testid={`pump-group-${g.key}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-white flex items-center gap-2">
                        <Fuel className="w-4 h-4 text-emerald-400" /> {g.grouped ? `Pump ${g.display}` : g.display}
                        <span className="text-xs font-normal text-[#9CA3AF]">· {g.hoses.length} hose{g.hoses.length === 1 ? "" : "s"}</span>
                      </span>
                      {g.grouped && (
                        <button onClick={() => delPumpGroup(s.id, g.pump_no)} data-testid={`delete-pump-group-${g.key}`}
                          className="text-[#9CA3AF] hover:text-red-400 text-xs flex items-center gap-1">
                          <Trash2 className="w-3.5 h-3.5" /> Delete pump
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                      {g.hoses.map((p) => (
                        <PumpCard key={p.id} pump={p} onSaved={load} onDelete={delPump} onEdit={openEdit} totals={(hoseTotals[s.id] || {})[p.id]} month={totalsMonth} />
                      ))}
                    </div>
                  </div>
                ))}
                {!(pumps[s.id] || []).length && <div className="text-sm text-[#9CA3AF]">No pumps configured.</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Load historical data — only when the IQ Network station exists */}
      {stations.some((s) => s.name === "IQ Network" || s.code === "IQ") && (
      <div className="rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.04] p-5 sm:p-6" data-testid="seed-zone">
        <h3 className="font-[Outfit] font-semibold text-lg text-emerald-400">Load Historical Data</h3>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mt-2">
          <p className="text-sm text-[#9CA3AF] max-w-xl">
            Import the RON95 &amp; Diesel records for <b className="text-white">June–August 2026</b> into this
            environment. Creates a station and pumps if none exist. Safe to run again — it only replaces those months.
          </p>
          <Button onClick={loadHistorical} disabled={seeding} data-testid="load-historical-button"
            className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold shrink-0">
            {seeding ? "Loading…" : "Load Jun–Aug 2026 data"}
          </Button>
        </div>
      </div>
      )}

      {/* Data repair — reconcile orphaned / mis-filed station data */}
      <div className="rounded-2xl border border-amber-500/25 bg-amber-500/[0.04] p-5 sm:p-6" data-testid="data-repair-zone">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h3 className="font-[Outfit] font-semibold text-lg text-amber-400 flex items-center gap-2"><Wrench className="w-5 h-5" /> Data Repair</h3>
            <p className="text-sm text-[#9CA3AF] max-w-xl mt-1">
              Find records staff entered under an old or deleted station (so a manager can&apos;t see some dates) and move them into the correct station so everyone sees the same data.
            </p>
          </div>
          <Button onClick={loadAudit} disabled={auditLoading} data-testid="run-audit-button"
            className="bg-amber-500 hover:bg-amber-400 text-black font-semibold shrink-0">
            {auditLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Scan data"}
          </Button>
        </div>

        {audit && (
          <div className="mt-4 space-y-4" data-testid="audit-results">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="audit-stations-table">
                <thead><tr className="text-left text-[#9CA3AF] text-xs uppercase font-mono tracking-wider border-b border-[#1F2937]">
                  <th className="py-2 pr-4 font-medium">Station</th><th className="py-2 pr-4 font-medium">Records</th><th className="py-2 pr-4 font-medium">Dates</th></tr></thead>
                <tbody>
                  {audit.stations.map((s) => (
                    <tr key={s.station_id} className="border-b border-[#1F2937]/60" data-testid={`audit-station-${s.station_id}`}>
                      <td className="py-2 pr-4 text-white">{s.station} <span className="text-xs font-mono text-emerald-400">{s.code}</span></td>
                      <td className="py-2 pr-4 metric-value text-white/90">{s.total}</td>
                      <td className="py-2 pr-4 text-xs text-[#9CA3AF]">{s.date_range ? `${s.date_range.from} → ${s.date_range.to}` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {audit.orphans.length ? (
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-4" data-testid="audit-orphans">
                <div className="text-sm font-semibold text-amber-300 mb-2">{audit.orphans.length} orphan data group{audit.orphans.length === 1 ? "" : "s"} — not linked to any current station</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm"><thead><tr className="text-left text-[#9CA3AF] text-xs uppercase font-mono tracking-wider border-b border-amber-500/20">
                    <th className="py-2 pr-4 font-medium">Old station id</th><th className="py-2 pr-4 font-medium">Records</th><th className="py-2 pr-4 font-medium">Dates</th></tr></thead>
                    <tbody>
                      {audit.orphans.map((o) => (
                        <tr key={o.station_id} className="border-b border-amber-500/10" data-testid={`orphan-${o.station_id}`}>
                          <td className="py-2 pr-4 font-mono text-xs text-white/80">{o.station_id}</td>
                          <td className="py-2 pr-4 metric-value text-white/90">{o.total}</td>
                          <td className="py-2 pr-4 text-xs text-[#9CA3AF]">{o.date_range ? `${o.date_range.from} → ${o.date_range.to}` : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : <div className="text-sm text-emerald-400" data-testid="audit-no-orphans">No orphaned data — every record is linked to a current station.</div>}

            <div className="flex flex-wrap items-end gap-3 border-t border-[#1F2937] pt-4">
              <div>
                <Label className="text-[#9CA3AF] text-xs">Move data FROM</Label>
                <Select value={reFrom} onValueChange={setReFrom}>
                  <SelectTrigger data-testid="reassign-from" className="mt-1.5 bg-[#111827] border-[#374151] text-white w-64"><SelectValue placeholder="Source (old / any station)" /></SelectTrigger>
                  <SelectContent className="bg-[#111827] border-[#374151] text-white">
                    {[...audit.orphans, ...audit.stations].map((o) => (
                      <SelectItem key={o.station_id} value={o.station_id}>{o.orphan ? `⚠ orphan ${o.station_id.slice(0, 8)}… (${o.total})` : `${o.station} (${o.total})`}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <ArrowRight className="w-5 h-5 text-[#6B7280] mb-2.5" />
              <div>
                <Label className="text-[#9CA3AF] text-xs">INTO</Label>
                <Select value={reTo} onValueChange={setReTo}>
                  <SelectTrigger data-testid="reassign-to" className="mt-1.5 bg-[#111827] border-[#374151] text-white w-64"><SelectValue placeholder="Target station" /></SelectTrigger>
                  <SelectContent className="bg-[#111827] border-[#374151] text-white">
                    {audit.stations.map((s) => <SelectItem key={s.station_id} value={s.station_id}>{s.station} {s.code}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={runReassign} disabled={reassigning || !reFrom || !reTo} data-testid="reassign-button"
                className="bg-amber-500 hover:bg-amber-400 text-black font-semibold">
                {reassigning ? <Loader2 className="w-4 h-4 animate-spin" /> : "Move & merge"}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Danger zone */}
      <div className="rounded-2xl border border-red-500/30 bg-red-500/[0.04] p-5 sm:p-6" data-testid="danger-zone">
        <h3 className="font-[Outfit] font-semibold text-lg text-red-400">Danger Zone</h3>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mt-2">
          <p className="text-sm text-[#9CA3AF] max-w-xl">
            Permanently delete <b className="text-white">all</b> stations, pumps, meter readings, deliveries, cash,
            expenses, price history and staff invites on <b className="text-white">this environment</b>. Your manager
            account is kept. Use this on the live site to remove the old demo data.
          </p>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" data-testid="clear-data-button"
                className="border-red-500/40 bg-red-500/10 text-red-400 hover:bg-red-500/20 shrink-0">
                Clear all data
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-[#111827] border-[#374151] text-white">
              <AlertDialogHeader>
                <AlertDialogTitle>Clear all station data?</AlertDialogTitle>
                <AlertDialogDescription className="text-[#9CA3AF]">
                  This cannot be undone. Everything except your manager login will be permanently removed from this
                  environment's database.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="mt-1">
                <Label className="text-[#9CA3AF] text-xs">Enter your manager password to confirm</Label>
                <Input type="password" value={wipePw} onChange={(e) => setWipePw(e.target.value)}
                  data-testid="clear-data-password" placeholder="Manager password"
                  className="mt-1.5 bg-[#0B0F17] border-[#374151] text-white" />
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel onClick={() => setWipePw("")} className="bg-transparent border-[#374151] text-white hover:bg-[#1F2937]">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={resetData} disabled={wiping || !wipePw} data-testid="confirm-clear-data"
                  className="bg-red-500 hover:bg-red-400 text-white">
                  {wiping ? "Clearing…" : "Yes, delete everything"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Add pump dialog */}
      <Dialog open={pumpOpen} onOpenChange={setPumpOpen}>
        <DialogContent className="bg-[#111827] border-[#374151] text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add Pump</DialogTitle></DialogHeader>
          <form onSubmit={createPump} className="space-y-4" data-testid="pump-form">
            <div><Label className="text-[#9CA3AF]">Pump Number</Label>
              <Input data-testid="pump-no-input" required value={pumpForm.pump_no}
                onChange={(e) => setPumpForm({ ...pumpForm, pump_no: e.target.value })} placeholder="e.g. 1"
                className="mt-1.5 bg-[#0B0F17] border-[#374151]" /></div>
            <div className="space-y-3">
              {pumpForm.hoses.map((h, i) => (
                <div key={i} className="rounded-xl border border-[#1F2937] p-3" data-testid={`hose-row-${i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-emerald-400">Hose {i + 1}</span>
                    {pumpForm.hoses.length > 1 && (
                      <button type="button" onClick={() => removeHose(i)} data-testid={`remove-hose-${i}`} className="text-[#9CA3AF] hover:text-red-400"><X className="w-4 h-4" /></button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-[#9CA3AF] text-xs">Hose label</Label>
                      <Input data-testid={`hose-label-${i}`} value={h.label} onChange={(e) => setHose(i, { label: e.target.value })} placeholder={`Hose ${i + 1}`} className="mt-1 bg-[#0B0F17] border-[#374151]" /></div>
                    <div><Label className="text-[#9CA3AF] text-xs">Product</Label>
                      <Select value={h.product} onValueChange={(v) => setHose(i, { product: v })}>
                        <SelectTrigger data-testid={`hose-product-${i}`} className="mt-1 bg-[#0B0F17] border-[#374151]"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#111827] border-[#374151] text-white">{PRODUCTS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                      </Select></div>
                    <div><Label className="text-[#9CA3AF] text-xs">Tank number</Label>
                      <Input data-testid={`hose-tank-${i}`} value={h.tank_number} onChange={(e) => setHose(i, { tank_number: e.target.value })} placeholder="e.g. 1" className="mt-1 bg-[#0B0F17] border-[#374151]" /></div>
                    <div><Label className="text-[#9CA3AF] text-xs">Tank (L)</Label>
                      <Input type="number" data-testid={`hose-capacity-${i}`} value={h.tank_capacity} onChange={(e) => setHose(i, { tank_capacity: e.target.value })} className="mt-1 bg-[#0B0F17] border-[#374151]" /></div>
                    <div><Label className="text-[#9CA3AF] text-xs">Retail /L</Label>
                      <Input type="number" step="0.001" data-testid={`hose-retail-${i}`} value={h.retail_price} onChange={(e) => setHose(i, { retail_price: e.target.value })} className="mt-1 bg-[#0B0F17] border-[#374151]" /></div>
                    <div><Label className="text-[#9CA3AF] text-xs">Cost /L</Label>
                      <Input type="number" step="0.001" data-testid={`hose-cost-${i}`} value={h.purchase_price} onChange={(e) => setHose(i, { purchase_price: e.target.value })} className="mt-1 bg-[#0B0F17] border-[#374151]" /></div>
                  </div>
                </div>
              ))}
            </div>
            {pumpForm.hoses.length < 4 && (
              <Button type="button" variant="outline" onClick={addHose} data-testid="add-hose-button" className="w-full border-[#374151] bg-transparent hover:bg-[#1F2937] text-white"><Plus className="w-4 h-4 mr-1" /> Add hose ({pumpForm.hoses.length}/4)</Button>
            )}
            <DialogFooter>
              <Button type="submit" data-testid="pump-submit-button" className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">Add Pump</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit pump dialog (manager only) */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-[#111827] border-[#374151] text-white">
          <DialogHeader><DialogTitle>Edit Pump</DialogTitle></DialogHeader>
          <form onSubmit={savePumpEdit} className="space-y-4" data-testid="pump-edit-form">
            <div><Label className="text-[#9CA3AF]">Label</Label>
              <Input data-testid="edit-pump-label-input" required value={editForm.label}
                onChange={(e) => setEditForm({ ...editForm, label: e.target.value })}
                className="mt-1.5 bg-[#0B0F17] border-[#374151]" /></div>
            <div><Label className="text-[#9CA3AF]">Product</Label>
              <Select value={editForm.product} onValueChange={(v) => setEditForm({ ...editForm, product: v })}>
                <SelectTrigger data-testid="edit-pump-product-select" className="mt-1.5 bg-[#0B0F17] border-[#374151]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#111827] border-[#374151] text-white">
                  {PRODUCTS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div><Label className="text-[#9CA3AF]">Pump Number</Label>
              <Input data-testid="edit-pump-no-input" value={editForm.pump_no}
                onChange={(e) => setEditForm({ ...editForm, pump_no: e.target.value })} placeholder="e.g. 1"
                className="mt-1.5 bg-[#0B0F17] border-[#374151]" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[#9CA3AF] text-xs">Tank Number</Label>
                <Input data-testid="edit-pump-tank-number-input" value={editForm.tank_number}
                  onChange={(e) => setEditForm({ ...editForm, tank_number: e.target.value })} placeholder="e.g. 1"
                  className="mt-1.5 bg-[#0B0F17] border-[#374151]" /></div>
              <div><Label className="text-[#9CA3AF] text-xs">Tank Capacity (L)</Label>
                <Input type="number" data-testid="edit-pump-tank-capacity-input" value={editForm.tank_capacity}
                  onChange={(e) => setEditForm({ ...editForm, tank_capacity: e.target.value })}
                  className="mt-1.5 bg-[#0B0F17] border-[#374151]" /></div>
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="edit-pump-submit-button" className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">Save changes</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
